import lodash from 'lodash';
import { saveAuthCredentials, getAuthCredentials, destroyAuthCredentials } from 'common/util/auth-storage';
import { EventEmitter } from 'events';
import { incomingDocumentConversionStatus, activeDocumentConversions } from 'common/actions/fileUploadActions';

const MINIMUM_REFRESH_INTERVAL = 90 * 1000; // 90 Seconds
const SESSION_TIMEOUT_WARNING = 18 * 60 * 1000; // 18 Minutes
const DELAY_TO_ALLOW_SESSION_TIMEOUT_LOGOUT_MODAL_TO_REGISTER = 200;
const DEFAULT_LOGOUT_URL = '/?expiredSession=true';

export class SessionTunnel extends EventEmitter {
    constructor(store, socket) {
        super();
        this.store = store;
        this.dispatch = store.dispatch;
        this.refreshSession = lodash.throttle(this.refreshSession.bind(this), MINIMUM_REFRESH_INTERVAL);
        this.socket = socket;
        this.sessionTimeoutWarningTimer;
        this.sessionExpirationTimer;
        this.sessionTimeoutWarning = SESSION_TIMEOUT_WARNING;
        this.logoutLandingPage = DEFAULT_LOGOUT_URL;
        this.tunnelActionsMap = {
            'logout': this.processLogout,
            'sessionExtended': this.onSessionExtended,
            'changeTerminal': () => window.location.pathname = '/',
            'activeDocumentConversions': (statuses) => this.dispatch(activeDocumentConversions(statuses)),
            'incomingDocumentConversion': (status) => this.dispatch(incomingDocumentConversionStatus(status))
        };
    }

    obtainAuthCredentials() {
        return getAuthCredentials();
    }

    setSSOLogoutLandingPage(logoutUrl) {
        const authCredentials = this.obtainAuthCredentials();
        this.logoutLandingPage = !lodash.isNil(logoutUrl) ? logoutUrl : DEFAULT_LOGOUT_URL;

        lodash.assign(authCredentials, { logoutLandingPage: this.logoutLandingPage });
        saveAuthCredentials(authCredentials);
    }

    refreshSession() {
        const authCredentials = this.obtainAuthCredentials();
        if (authCredentials) {
            this.socket.publishToChannel('refreshSession', { ...authCredentials, action: 'refreshSession' });
            clearTimeout(this.sessionTimeoutWarningTimer);
            clearTimeout(this.sessionExpirationTimer);
        }
    }

    expireSession() {
        const authCredentials = this.obtainAuthCredentials();
        if (authCredentials) {
            this.socket.publishToChannel('expireSession', { ...authCredentials, action: 'expireSession' });
            clearTimeout(this.sessionTimeoutWarningTimer);
            clearTimeout(this.sessionExpirationTimer);
        }
    }

    changeTerminal(previousUserAuthCreds) {
        this.socket.publishToChannel('changeTerminal', { ...previousUserAuthCreds, action: 'changeTerminal' });
        clearTimeout(this.sessionTimeoutWarningTimer);
        clearTimeout(this.sessionExpirationTimer);
    }

    async checkSession() {
        const authCredentials = this.obtainAuthCredentials();
        const sessionIsValid = !lodash.isNil(authCredentials) ? await this.socket.checkSession(authCredentials) : false;
        if (!sessionIsValid) {
            this.constructor.logout(this.logoutLandingPage);
        }
        return sessionIsValid;
    }

    async initializeSessionTunnel() {
        const { sub, accessToken } = this.obtainAuthCredentials();
        this.socket.initSocket({ sub, accessToken });
        this.socket.subscribeToChannel('connect_error', (error) => {
            if (error.message === 'Unauthorized') {
                this.constructor.logout(this.logoutLandingPage);
            }
        });
        const validSession = await this.checkSession();
        if (validSession) {
            this.setSessionTimeoutWarning();
            this.socket.subscribeToChannel(sub, this.actionDistribution.bind(this));
            this.retrieveActiveDocumentConversions();
        }
    }

    actionDistribution({ action, data, sub }) {
        this.tunnelActionsMap[action].call(this, data);
    }

    retrieveActiveDocumentConversions() {
        const authCredentials = this.obtainAuthCredentials();
        if (authCredentials) {
            this.socket.publishToChannel('retrieveDocumentConversions', { ...authCredentials, action: 'retrieveDocumentConversions' });
        }
    }

    setSessionTimeoutWarning(sessionTimeoutWarning) {
        // clear any active session timeout warnings
        clearTimeout(this.sessionTimeoutWarningTimer);
        clearTimeout(this.sessionExpirationTimer);

        const sessionTtl = sessionTimeoutWarning !== undefined ? sessionTimeoutWarning : this.sessionTimeoutWarning;
        this.sessionTimeoutWarning = sessionTtl;
        this.sessionTimeoutWarningTimer = setTimeout(() => this.emit('sessionTimeoutWarning'), sessionTtl);
        this.sessionExpirationTimer = setTimeout(() => this.constructor.logout(this.logoutLandingPage), sessionTtl + 120000);
    }

    onSessionExtended() {
        setTimeout(() => {
            this.emit('sessionExtended');
            clearTimeout(this.sessionTimeoutWarningTimer);
            clearTimeout(this.sessionExpirationTimer);
            this.setSessionTimeoutWarning();
        }, DELAY_TO_ALLOW_SESSION_TIMEOUT_LOGOUT_MODAL_TO_REGISTER);
    }

    async processLogout() {
        return this.checkSession();
    }

    static logout(logoutLandingPage = DEFAULT_LOGOUT_URL) {
        lodash.defer(() => {
            destroyAuthCredentials();
            window.location.replace(logoutLandingPage);
        });
    }

    subscribe() {
        window.addEventListener('keydown', this.refreshSession, true);
        window.addEventListener('mousedown', this.refreshSession, true);
    }

    unsubscribe() {
        window.removeEventListener('keydown', this.refreshSession, true);
        window.removeEventListener('mousedown', this.refreshSession, true);
    }
}
